import {
    Editor,
    Location,
    Range,
    Selection,
} from 'slate';

import { IResolveEditorSelectionTextResult } from '../interfaces';

interface IResolveEditorSelectionTextArgs {
    editor: Editor,
    selection: Selection | null,
    deriveSelection: () => Selection | null,
}

const tryResolveSelectionText = (
    editor: Editor,
    selection: Selection | null,
): IResolveEditorSelectionTextResult | null => {
    if (!selection || !Range.isRange(selection)) return null;

    try {
        const text = Editor.string(editor, selection as Location);
        if (text.trim().length === 0) return null;
        return {
            selection,
            text,
        };
    } catch (err) {
        return null;
    }
};

/**
 * Prefer the freshest DOM-derived selection during highlight capture, then
 * safely fall back to Slate's cached selection when the browser selection
 * cannot be resolved back into editor coordinates.
 */
const tryDeriveSelection = (
    deriveSelection: () => Selection | null,
): Selection | null => {
    try {
        return deriveSelection();
    } catch (err) {
        return null;
    }
};

function resolveEditorSelectionText({
    editor,
    selection,
    deriveSelection,
}: IResolveEditorSelectionTextArgs): IResolveEditorSelectionTextResult | null {
    const derivedSelection = tryDeriveSelection(deriveSelection);
    const nextDerivedSelection = tryResolveSelectionText(editor, derivedSelection);
    if (nextDerivedSelection) {
        return nextDerivedSelection;
    }

    return tryResolveSelectionText(editor, selection);
}

export default resolveEditorSelectionText;
